<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';

const props = defineProps({
    audioUrl: String,
    slides: Array
});

const audioPlayer = ref(null);
const currentTime = ref(0);
const playerContainer = ref(null);
const isFullscreen = ref(false);

const onTimeUpdate = () => {
    currentTime.value = audioPlayer.value.currentTime;
};

const currentSlide = computed(() => {
    return props.slides
        .filter(slide => slide.timestamp <= currentTime.value)
        .sort((a, b) => b.timestamp - a.timestamp)[0] || props.slides[0];
});

const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
        try {
            await playerContainer.value.requestFullscreen();
            isFullscreen.value = true;
        } catch (err) {
        }
    } else {
        try {
            await document.exitFullscreen();
            isFullscreen.value = false;
        } catch (err) {
        }
    }
};

const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
};

onMounted(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
    document.removeEventListener('fullscreenchange', handleFullscreenChange);
});
</script>

<template>
    <div class="training-player" ref="playerContainer">
        <div class="slide-viewer">
            <img :src="currentSlide.url" alt="Slide" class="slide-image" />
        </div>

        <audio
            class="audio-player mt-4"
            ref="audioPlayer"
            :src="audioUrl"
            controls
            @timeupdate="onTimeUpdate"
        ></audio>

        <button class="fullscreen-btn" @click="toggleFullscreen" :title="isFullscreen ? $t('trainings.labels.close_full_screen') : $t('trainings.labels.full_screen')">
            <b-icon v-if="!isFullscreen" icon="fullscreen"/>
            <b-icon v-else icon="fullscreen-exit"/>
        </button>
    </div>
</template>

<style scoped>
.training-player {
    position: relative;
}

.audio-player {
    width: 100%;
}

.slide-viewer {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    background-color: #000;
}

.slide-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.fullscreen-btn {
    position: absolute;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    color: white;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    z-index: 10;
}

.fullscreen-btn:hover {
    background: rgba(0, 0, 0, 0.8);
}

.training-player:fullscreen {
    background-color: #000;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 20px;
}

.training-player:fullscreen .slide-viewer {
    flex: 1;
    aspect-ratio: auto;
}

.training-player:fullscreen .audio-player {
    margin-top: 20px;
}
</style>
